import React, { useEffect, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

export default function HeroSection({ backgroundImages, currentImageIndex }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const handleNavigate = () => {
    navigate("/services");
  };

  const baseIconStyle = {
    filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.7))",
    transform: "translateZ(8px)",
    transition: "transform 0.3s ease",
    fontSize: 32,
    cursor: 'pointer',
    '&:hover': {
      transform: "translateZ(12px) scale(1.1)",
    }
  };

  // === Smoke Animation ===
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let particles = [];

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let mouse = { x: canvas.width / 2, y: canvas.height / 2 };

    window.addEventListener("mousemove", (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    });

    class Particle {
      constructor() {
        this.x = mouse.x;
        this.y = mouse.y;
        this.size = Math.random() * 5 + 2;
        this.speedX = Math.random() * 3 - 1.5;
        this.speedY = Math.random() * 3 - 1.5;
        this.alpha = 1;
        this.color = Math.random() > 0.5 ? "#CE8238" : "#0057A4";
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.alpha -= 0.01;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.alpha;

        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 6);
        gradient.addColorStop(0, this.color + "AA"); // Semi-transparent center
        gradient.addColorStop(1, this.color + "00"); // Fully transparent edge

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.push(new Particle());
      particles.forEach((p, i) => {
        p.update();
        p.draw();
        if (p.alpha <= 0) particles.splice(i, 1);
      });
      requestAnimationFrame(animate);
    }

    animate();

    window.addEventListener("resize", () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    });
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        position: "relative",
        borderBottom: "10px solid #CE8238",
        borderBottomRightRadius: "100px",
        borderBottomLeftRadius: "100px",
        overflow: "hidden",
        px: { xs: 4, md: 10 },
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Background Images */}
      {backgroundImages.map((bg, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: 1,
            opacity: currentImageIndex === index ? 1 : 0,
            transition: "opacity 1s ease-in-out",
          }}
        />
      ))}

      {/* Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.64)",
          zIndex: 2,
        }}
      />

      {/* Smoke Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 3,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />

      {/* Main Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 4,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1400px",
          gap: 4,
        }}
      >
        {/* Text Content */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2, mt: 4 }}>
            {t("Advancing Healthcare Through Innovation")}
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 500 }}>
            {t("Designing and developing cutting-edge medical equipment to improve patient care and clinical efficiency.")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 4, alignItems: "flex-start" }}>
            <Button
              variant="contained"
              onClick={handleNavigate}
              sx={{
                backgroundColor: "#CE8238",
                '&:hover': { backgroundColor: "#b56b2e" },
                color: "#fff",
              }}
            >
              {t("Discover Our Solutions")}
            </Button>

            {/* Social Icons and Email */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
              <Box sx={{ display: "flex", gap: 3 }}>
                <InstagramIcon
                  sx={{ ...baseIconStyle, color: "#E4405F" }}
                  onClick={() => window.open('https://instagram.com', '_blank')}
                />
                <FacebookIcon
                  sx={{ ...baseIconStyle, color: "#1877F2" }}
                  onClick={() => window.open('https://facebook.com', '_blank')}
                />
                <TwitterIcon
                  sx={{ ...baseIconStyle, color: "#1DA1F2" }}
                  onClick={() => window.open('https://twitter.com', '_blank')}
                />
                <WhatsAppIcon
                  sx={{ ...baseIconStyle, color: "#25D366" }}
                  onClick={() => window.open('https://wa.me/your-number', '_blank')}
                />
              </Box>

              {/* Email below icons */}
              <Box
                component="a"
                href="mailto:info@tatweer-med.com"
                sx={{
                  mt: 1,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#fff",
                  textDecoration: "none",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  px: 2,
                  py: 0.5,
                  borderRadius: "6px",
                  display: "inline-block",
                  boxShadow: "0 1px 3px rgba(226, 226, 226, 0.05)",
                }}
              >
                info@tatweer-med.com
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Side Image */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/image/Mobile.png"
            alt="Medical Innovation"
            sx={{
              maxWidth: "100%",
              maxHeight: "450px",
              borderRadius: "20px",
              boxShadow: "0 10px 20px rgba(0,0,0,0.5)",
              transition: "transform 0.3s",
              '&:hover': {
                transform: "scale(1.05)",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
